import { ownerDocument, areArraysEqual, asc } from '../../utils'

export function findClosest(values: number[], currentValue: number) {
  const { index: closestIndex } =
    values.reduce<{ distance: number; index: number } | null>(
      (acc, value: number, index: number) => {
        const distance = Math.abs(currentValue - value)

        if (
          acc === null ||
          distance < acc.distance ||
          distance === acc.distance
        ) {
          return {
            distance,
            index,
          }
        }

        return acc
      },
      null
    ) ?? {}
  return closestIndex
}

export function trackFinger(
  event: TouchEvent | MouseEvent | React.MouseEvent,
  touchId: React.RefObject<any>
) {
  // The event is TouchEvent
  if (touchId.current !== undefined && (event as TouchEvent).changedTouches) {
    const touchEvent = event as TouchEvent
    for (let i = 0; i < touchEvent.changedTouches.length; i += 1) {
      const touch = touchEvent.changedTouches[i]
      if (touch!.identifier === touchId.current) {
        return {
          x: touch!.clientX,
          y: touch!.clientY,
        }
      }
    }

    return false
  }

  // The event is MouseEvent
  return {
    x: (event as MouseEvent).clientX,
    y: (event as MouseEvent).clientY,
  }
}

export function valueToPercent(value: number, min: number, max: number) {
  return ((value - min) * 100) / (max - min)
}

export function percentToValue(percent: number, min: number, max: number) {
  return (max - min) * percent + min
}

export function getDecimalPrecision(num: number) {
  // This handles the case when num is very small (0.00000001), js will turn this into 1e-8.
  // When num is bigger than 1 or less than -1 it won't get converted to this notation so it's fine.
  if (Math.abs(num) < 1) {
    const parts = num.toExponential().split('e-')
    const matissaDecimalPart = parts[0]!.split('.')[1]
    return (
      (matissaDecimalPart ? matissaDecimalPart.length : 0) +
      parseInt(parts[1]!, 10)
    )
  }

  const decimalPart = num.toString().split('.')[1]
  return decimalPart ? decimalPart.length : 0
}

export function roundValueToStep(value: number, step: number, min: number) {
  const nearest = Math.round((value - min) / step) * step + min
  return Number(nearest.toFixed(getDecimalPrecision(step)))
}

export function setValueIndex({
  values,
  newValue,
  index,
}: {
  values: number[]
  newValue: number
  index: number
}) {
  const output = values.slice()
  output[index] = newValue
  return output.sort(asc)
}

export function focusThumb({
  sliderRef,
  activeIndex,
  setActive,
}: {
  sliderRef: React.RefObject<any>
  activeIndex: number
  setActive?: (num: number) => void
}) {
  const doc = ownerDocument(sliderRef.current)
  if (
    !sliderRef.current?.contains(doc.activeElement) ||
    Number(doc?.activeElement?.getAttribute('data-index')) !== activeIndex
  ) {
    sliderRef.current
      ?.querySelector(`[type="range"][data-index="${activeIndex}"]`)
      .focus()
  }

  if (setActive) {
    setActive(activeIndex)
  }
}

export function areValuesEqual(
  newValue: number | Array<number>,
  oldValue: number | Array<number>
): boolean {
  if (typeof newValue === 'number' && typeof oldValue === 'number') {
    return newValue === oldValue
  }
  if (typeof newValue === 'object' && typeof oldValue === 'object') {
    return areArraysEqual(newValue, oldValue)
  }
  return false
}
